import type { PumpSuspensionEvent } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  ActivityType,
  BolusState,
  PumpSuspendReason,
  PumpSuspendState,
} from "@tandemdiabetes/sugarmate-api-react-client"
import dayjs from "dayjs"
import { useMemo } from "react"

import styles from "./FeedItemHeader.module.scss"
import { FeedItemHeaderCarbSummary } from "./FeedItemHeaderCarbSummary"
import { FeedItemHeaderGlucoseSummary } from "./FeedItemHeaderGlucoseSummary"
import { FeedItemHeaderInsulinSummary } from "./FeedItemHeaderInsulinSummary"
import {
  UserEventsLoadedComponent,
  UserEntriesLoadedComponent,
} from "src/components"
import { useEntryModalPublicContext } from "src/components/EntryModal"
import { useEventModalPublicContext } from "src/components/EventModal"
import { usePolyglot } from "src/contexts"
import {
  useReadableDate,
  useReadableTime,
  useToday,
} from "src/hooks"
import type {
  ReadOnlyUserEvent,
  ReadOnlyUserEntry,
} from "src/types"

function isPumpSuspensionEvent(
  item: ReadOnlyUserEvent | ReadOnlyUserEntry,
): item is ReadOnlyUserEvent & PumpSuspensionEvent {
  return (
    "event_type" in item && item.event_type === ActivityType.PumpSuspension
  )
}

function isOngoingEvent(item: ReadOnlyUserEvent | ReadOnlyUserEntry): boolean {
  return (
    "event_type" in item &&
    ((item.event_type === ActivityType.PumpSuspension &&
      item.pump_suspension.state === PumpSuspendState.InProgress) ||
      (item.event_type === ActivityType.Bolus &&
        item.bolus.state === BolusState.InProgress))
  )
}

interface Props {
  userItem: ReadOnlyUserEvent | ReadOnlyUserEntry;
}

/*
 * this component renders the timestamp and starting and ending glucose values for an event or entry
 */
export function FeedItemHeader(props: Props) {
  const { userItem } = props
  const polyglot = usePolyglot()
  const today = useToday()
  const date = useReadableDate(userItem.started_at)
  const startTime = useReadableTime(userItem.started_at)
  const endTime = useReadableTime(userItem.ended_at)
  const timeLabel = useMemo(
    () => {
      let start = startTime
      if (isOngoingEvent(userItem)) {
        if (!dayjs(userItem.started_at).isSame(
          today,
          "day",
        )) {
          start = polyglot.t(
            "time.with_date",
            {
              time: startTime,
              date,
            },
          )
        }
        if (isPumpSuspensionEvent(userItem)) {
          // Display "10:00 PM, Yesterday — now" for ex for ongoing pump suspension events
          return polyglot.t(
            "time.range",
            {
              start,
              end: polyglot.t("time.now"),
            },
          )
        }
        // Display "10:00 PM, Yesterday" for ex for ongoing bolus events
        return start
      }

      if (
        isPumpSuspensionEvent(userItem) &&
        userItem.pump_suspension.suspend_reason !== PumpSuspendReason.PumpMalfunction
      ) {
        // Display "1:00 PM —- 2:00 PM" for ex for completed pump suspension events
        return polyglot.t(
          "time.range",
          {
            start,
            end: endTime,
          },
        )
      }

      return start
    },
    [
      userItem,
      startTime,
      endTime,
      date,
      today,
      polyglot,
    ],
  )

  const userEvent = userItem as ReadOnlyUserEvent
  const { openEntryModal } = useEntryModalPublicContext()
  const { openEventModal } = useEventModalPublicContext()

  const userEntry = userItem as ReadOnlyUserEntry
  const onClick = useMemo(
    () => {
      if (userEvent.event_type) {
        // Only open the EventModal for supported event types (e.g., DeviceChange).
        if (
          userEvent.event_type === ActivityType.DeviceChange ||
          userEvent.event_type === ActivityType.Bolus
        ) {
          return () => {
            openEventModal({
              userEvent,
            })
          }
        }

        // Unsupported event types: no click handler, keep button disabled.
        return undefined
      }

      // No event_type means this is a user entry: open the EntryModal.
      return () => {
        const onlySubEntry =
          userEntry.subentries.length === 1 ? userEntry.subentries[0] : undefined

        openEntryModal({
          userEntry,
          subEntry: onlySubEntry,
        })
      }
    },
    [
      userEntry,
      userEvent,
      openEntryModal,
      openEventModal,
    ],
  )

  return (
    <button
      className={styles.container}
      onClick={onClick}
      disabled={!onClick}
    >
      <div
        className={styles.time}
        id="feed-time"
      >
        {timeLabel}
      </div>
      <FeedItemHeaderInsulinSummary
        className={styles.summary}
        userItem={userItem}
      />
      <FeedItemHeaderCarbSummary
        className={styles.summary}
        userItem={userItem}
      />
      <UserEventsLoadedComponent>
        <UserEntriesLoadedComponent>
          <FeedItemHeaderGlucoseSummary
            className={styles.summary}
            userItem={userItem}
          />
        </UserEntriesLoadedComponent>
      </UserEventsLoadedComponent>
    </button>
  )
}
